import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { PrintDataEntitas } from "@/domain/entities/Booking";
import dateToDateString from "@/app/infrastructures/misc/common-library/DateToDateString";
import startCase from "lodash/startCase";
import { createI18n } from "vue-i18n";
import en from "@/i18n/locales/en";
import id from "@/i18n/locales/id";
import my from "@/i18n/locales/my";
import {
  MultilingualText,
  renderMultilingualText
} from "@/app/infrastructures/misc/js-pdf copy/render-multilingual-text";
import {
  ellipsisString,
  formatByCountry
} from "@/app/infrastructures/misc/Utils";
import { limitLengthName } from "../receipt-v2/module";
import { MainAppController } from "@/app/ui/controllers/MainAppController";

const formatPrice = (value: number, country: string) =>
  formatByCountry(value, {
    type: "money",
    country: country
  });

const formatWeight = (value: number, country: string) =>
  formatByCountry(value, {
    type: "weight",
    country: country
  });

const formatQuantity = (value: number, country: string) =>
  formatByCountry(value, {
    type: "quantity",
    country: country
  });

// Helper to render the header section
function renderHeader(doc: any, translate: any, logo: string, scale: number) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16 * scale);
  doc.setTextColor("#4d4d4d");
  doc.text("COMMERCIAL INVOICE", 7 * scale, 12 * scale);
  doc.addImage(logo, "PNG", 135 * scale, 15 * scale, 60 * scale, 14 * scale, "logo", "FAST");
}

// Helper to render shipper info
async function renderShipperInfo(
  doc: any,
  translate: any,
  headerData: PrintDataEntitas,
  language: string,
  scale: number
) {
  const fs = 10 * scale;
  const lineH = 5 * scale;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(fs);
  doc.text(startCase(translate("invoice.date")), 7 * scale, 23 * scale);
  doc.setFont("helvetica", "normal");
  doc.text(":", 40 * scale, 23 * scale);
  doc.text(
    dateToDateString(headerData.sttCreatedAt, false, false, language),
    42 * scale,
    23 * scale
  );

  doc.setFont("helvetica", "bold");
  doc.setFontSize(fs);
  doc.text("STT", 7 * scale, 30 * scale);
  doc.setFont("helvetica", "normal");
  doc.text(":", 40 * scale, 30 * scale);
  doc.text(headerData.sttNo, 42 * scale, 30 * scale);

  doc.setFillColor("#EBECF0");
  doc.rect(7 * scale, 40 * scale, 195 * scale, 0.2 * scale, "F");

  let y = 47 * scale;
  const xLabel = 7 * scale;
  const xColon = 40 * scale;
  const xValue = 42 * scale;

  // Shipper Name
  doc.setFont("helvetica", "bold");
  doc.setFontSize(fs);
  doc.text(startCase(translate("invoice.shipperName")), xLabel, y);
  doc.setFont("helvetica", "normal");
  doc.text(":", xColon, y);

  const senderBlock = await renderMultilingualText(
    new MultilingualText({
      doc,
      text: (headerData.sttSenderName?.trim() || "-").toUpperCase(),
      product: headerData.sttProductType,
      isInternational: headerData.isInternational,
      startX: xValue,
      startY: y,
      maxWidth: 45 * scale,
      fontSize: fs,
      fontStyle: "bold"
    })
  );

  y += senderBlock.height + (1 * scale);

  // Shipper Phone
  doc.setFont("helvetica", "bold");
  doc.setFontSize(fs);
  doc.text(startCase(translate("invoice.shipperPhone")), xLabel, y);
  doc.setFont("helvetica", "normal");
  doc.text(":", xColon, y);
  const shipperPhoneLines =
    headerData.sttSenderPhone && headerData.sttSenderPhone.trim() !== ""
      ? doc.splitTextToSize(headerData.sttSenderPhone, 45 * scale)
      : ["-"];
  doc.text(shipperPhoneLines, xValue, y);
  y += shipperPhoneLines.length * lineH;

  // Shipper Address
  doc.setFont("helvetica", "bold");
  doc.setFontSize(fs);
  doc.text(startCase(translate("invoice.shipperAddress")), xLabel, y);
  doc.setFont("helvetica", "normal");
  doc.text(":", xColon, y);

  const addressLines = ellipsisString(
    headerData.sttOriginCityName.toUpperCase(),
    200
  );
  const addressBlock = await renderMultilingualText(
    new MultilingualText({
      doc,
      text: addressLines,
      product: headerData.sttProductType,
      isInternational: headerData.isInternational,
      startX: xValue,
      startY: y,
      maxWidth: 50 * scale,
      fontSize: fs,
      fontStyle: "bold"
    })
  );

  y += addressBlock.height + (0.5 * scale);

  // Duty & Tax Terms
  doc.setFont("helvetica", "bold");
  doc.setFontSize(fs);
  doc.text(translate("invoice.dutyTaxTerms"), xLabel, y);
  doc.setFont("helvetica", "normal");
  doc.text(":", xColon, y);
  doc.text(headerData.sttDutyTaxTerms, xValue, y);
  y += 5;

  return y; // ⬅ Tambahkan ini
}

// Helper to render consignee info
async function renderConsigneeInfo(
  doc: any,
  translate: any,
  headerData: PrintDataEntitas,
  scale: number
) {
  const fs = 10 * scale;
  const lineH = 5 * scale;
  let y = 47 * scale;
  const xLabel = 95 * scale;
  const xColon = 132 * scale;
  const xValue = 135 * scale;

  // conssigneeID
  doc.setFont("helvetica", "bold");
  doc.setFontSize(fs);
  doc.text(startCase(translate("invoice.conssigneeID")), xLabel, y);
  doc.setFont("helvetica", "normal");
  doc.text(":", xColon, y);
  doc.text(
    headerData.sttIdentityNumber && headerData.sttIdentityNumber.trim() !== ""
      ? headerData.sttIdentityNumber
      : "-",
    xValue,
    y
  );
  y += lineH;

  // conssigneeName
  doc.setFont("helvetica", "bold");
  doc.setFontSize(fs);
  doc.text(startCase(translate("invoice.conssigneeName")), xLabel, y);
  doc.setFont("helvetica", "normal");
  doc.text(":", xColon, y);

  const recipientBlock = await renderMultilingualText(
    new MultilingualText({
      doc,
      text: (headerData.sttRecipientName?.trim() || "-").toUpperCase(),
      product: headerData.sttProductType,
      isInternational: headerData.isInternational,
      startX: xValue,
      startY: y,
      maxWidth: 60 * scale,
      fontSize: fs,
      fontStyle: "bold"
    })
  );

  y += recipientBlock.height + (1 * scale);

  // conssigneeEmail
  doc.setFont("helvetica", "bold");
  doc.setFontSize(fs);
  doc.text(startCase(translate("invoice.conssigneeEmail")), xLabel, y);
  doc.setFont("helvetica", "normal");
  doc.text(":", xColon, y);
  const emailLines = doc.splitTextToSize(
    headerData.sttRecipientEmail.toUpperCase() || "-",
    60 * scale
  );
  doc.text(emailLines, xValue, y);
  y += emailLines.length * lineH;

  // conssigneeAddress
  doc.setFont("helvetica", "bold");
  doc.setFontSize(fs);
  doc.text(startCase(translate("invoice.conssigneeAddress")), xLabel, y);
  doc.setFont("helvetica", "normal");
  doc.text(":", xColon, y);

  const address = `${headerData.sttRecipientAddress.toUpperCase() ||
    "-"}.\n${headerData.sttDestinationDistrictName.toUpperCase() || "-"}`;
  const addressLines = ellipsisString(address, 200);
  const addressBlock = await renderMultilingualText(
    new MultilingualText({
      doc,
      text: addressLines,
      product: headerData.sttProductType,
      isInternational: headerData.isInternational,
      startX: xValue,
      startY: y,
      maxWidth: 60 * scale,
      fontSize: fs,
      fontStyle: "bold"
    })
  );

  y += addressBlock.height + (0.5 * scale);

  // postCode
  doc.setFont("helvetica", "bold");
  doc.setFontSize(fs);
  doc.text(startCase(translate("invoice.postCode")), xLabel, y);
  doc.setFont("helvetica", "normal");
  doc.text(":", xColon, y);
  const postCodeLines = doc.splitTextToSize(
    headerData.sttPostalCode || "-",
    60 * scale
  );
  doc.text(postCodeLines, xValue, y);
  y += postCodeLines.length * lineH * 0.8;
  return y;
}

// Helper to get main table data
function getMainTableData(headerData: PrintDataEntitas) {
  if (!headerData.sttCipl) {
    return [];
  }

  return headerData.sttCipl.map((item, index) => {
    const total = item.item_price * item.quantity;
    return {
      no: index + 1,
      itemCommodity: item.item_detail_en || "-",
      itemHSCODE: item.commodity_hs_code || "-",
      itemUnitValueGoods:
        item.item_price != null
          ? `${headerData.currencyCode} ${formatPrice(
              item.item_price,
              headerData.sttDestinationCountryCode
            )}`
          : "-",
      itemTotalItem:
        item.quantity != null
          ? formatQuantity(item.quantity, headerData.sttDestinationCountryCode)
          : "-",
      itemTotalValueGoods:
        item.item_price != null && item.quantity != null
          ? `${headerData.currencyCode}${formatPrice(
              total,
              headerData.sttDestinationCountryCode
            )}`
          : "-"
    };
  });
}

function getMainTableColumns(translate: any) {
  return [
    { header: "No.", dataKey: "no" },
    {
      header: startCase(translate("invoice.commodity")),
      dataKey: "itemCommodity"
    },
    { header: startCase(translate("invoice.HSCODE")), dataKey: "itemHSCODE" },
    {
      header: startCase(translate("invoice.unitValueGoods")),
      dataKey: "itemUnitValueGoods"
    },
    {
      header: startCase(translate("invoice.totalItem")),
      dataKey: "itemTotalItem"
    },
    {
      header: startCase(translate("invoice.totalValueGoods")),
      dataKey: "itemTotalValueGoods"
    }
  ];
}

function getMainTableColumnStyles(scale: number) {
  return {
    0: { cellWidth: 13 * scale },
    1: { cellWidth: 53 * scale },
    2: { cellWidth: 40 * scale },
    3: { cellWidth: 30 * scale },
    4: { cellWidth: 30 * scale },
    5: { cellWidth: 30 * scale }
  };
}

function handleWillDrawCell(
  dataCell: any,
  dataLength: number,
  lastYRef: { value: number },
  lastHeightRef: { value: number },
  doc: any,
  scale: number
) {
  if (dataCell.row.index < 0) return;
  if (
    dataCell.column.dataKey === "itemCommodity" ||
    dataCell.column.dataKey === "no"
  ) {
    dataCell.cell.styles.halign = "left";
  }
  lastYRef.value = dataCell.cursor.y;
  lastHeightRef.value = dataCell.row.height;
  const pageHeight = doc.internal.pageSize.height;
  if (
    dataCell.row.index === dataLength - 2 &&
    lastYRef.value + lastHeightRef.value + (20 * scale) > pageHeight
  ) {
    dataCell.cell.contentHeight = 100 * scale;
    dataCell.row.height = 100 * scale;
  }
}

async function renderMainTable(
  doc: any,
  translate: any,
  headerData: PrintDataEntitas,
  language: string,
  scale: number
) {
  let lastY = 0;
  const lastHeight = 0;
  const lastYRef = { value: lastY };
  const lastHeightRef = { value: lastHeight };
  const data = getMainTableData(headerData);
  const dataLength = data.length;
  const columnsTable = getMainTableColumns(translate);
  const columnStyles = getMainTableColumnStyles(scale);

  const lastYShipper = await renderShipperInfo(
    doc,
    translate,
    headerData,
    language,
    scale
  );
  const lastYConsignee = await renderConsigneeInfo(doc, translate, headerData, scale);

  const dynamicStartY = Math.max(lastYShipper, lastYConsignee) + (2 * scale);

  doc.autoTable({
    body: data,
    columns: columnsTable,
    theme: "plain",
    startY: dynamicStartY,
    margin: { top: 7 * scale, left: 7 * scale, right: 7 * scale, bottom: 7 * scale },
    headStyles: {
      font: "helvetica",
      fontStyle: "bold",
      fillColor: "#F5F5F5",
      textColor: "#1A1421",
      fontSize: 8 * scale,
      halign: "center",
      valign: "top",
      cellPadding: { top: 4 * scale, right: 2 * scale, bottom: 4 * scale, left: 2 * scale }
    },
    bodyStyles: {
      font: "helvetica",
      fontStyle: "normal",
      fillColor: "#fff",
      textColor: "#4D4D4D",
      fontSize: 8 * scale,
      halign: "center",
      valign: "top",
      lineColor: "#e0e0e0",
      lineWidth: { top: 0.1 * scale, right: 0, bottom: 0.1 * scale, left: 0 },
      cellPadding: { top: 3 * scale, right: 2 * scale, bottom: 3 * scale, left: 2 * scale },
      cellWidth: "auto"
    },
    columnStyles: columnStyles,
    willDrawCell: (dataCell: any) =>
      handleWillDrawCell(dataCell, dataLength, lastYRef, lastHeightRef, doc, scale)
  });

  lastY = lastYRef.value + lastHeightRef.value + (1 * scale);

  return { lastY, dataLength };
}

// Helper to render terms and signature
function getTermsAndSignatureColumns() {
  return [{ dataKey: "term" }, { dataKey: "sign" }];
}

function getTermsAndSignatureColumnStyles(scale: number) {
  return {
    0: { cellWidth: 140 * scale },
    1: { cellWidth: 60 * scale }
  };
}

function getTermsAndSignatureBody(
  translate: any,
  headerData: PrintDataEntitas
) {
  return [
    {
      term: {
        content: translate("invoice.note"),
        styles: {
          textColor: "#4D4D4D",
          valign: "middle", // ubah dari "top" ke "middle"
          halign: "left"
        }
      },
      sign: {
        content: `${startCase(
          translate("invoice.totalWeight")
        )}: ${formatWeight(
          headerData.sttGrossWeight,
          headerData.sttBookedByCountry
        )} kg`,
        styles: {
          fontStyle: "bold",
          textColor: "#1A1421",
          halign: "right",
          valign: "middle" // ubah dari "top" ke "middle"
        }
      }
    },
    {
      term: {
        content: translate("invoice.term"),
        styles: {
          textColor: "#4D4D4D",
          halign: "left",
          valign: "top"
        }
      },
      sign: {
        content: "", // kosongkan agar tidak ada content di kanan
        styles: {}
      }
    }
  ];
}

function handleTermsAndSignatureWillDrawCell(
  data: any,
  lastYRef: { value: number },
  lastHeightRef: { value: number }
) {
  if (data.row.index < 0) return;
  if (data.row.index === 0 && data.column.dataKey === "term") {
    data.cell.styles.fontStyle = "bold";
  }
  if (data.column.dataKey === "sign" || data.row.section === "body") {
    data.row.cells["sign"].styles = {
      ...data.row.cells["sign"].styles,
      fontStyle: "bold"
    };
  }

  lastYRef.value = data.cursor.y;
  lastHeightRef.value = data.row.height;
}

function renderTerms(
  doc: any,
  translate: any,
  headerData: PrintDataEntitas,
  lastY: number,
  scale: number
) {
  const pageHeight = doc.internal.pageSize.height;
  const estimatedSignatureHeight = 40 * scale;
  const spaceBelowTable = pageHeight - lastY;

  const dynamicGap = spaceBelowTable > estimatedSignatureHeight + (20 * scale) ? 5 * scale : 90 * scale;
  const actualStartY = Math.max(lastY + dynamicGap);

  const lastHeight = 0;
  const lastYRef = { value: lastY };
  const lastHeightRef = { value: lastHeight };

  doc.autoTable({
    body: getTermsAndSignatureBody(translate, headerData),
    columns: getTermsAndSignatureColumns(),
    theme: "plain",
    startY: actualStartY,
    margin: { top: 7 * scale, left: 7 * scale, right: 0, bottom: 7 * scale },
    bodyStyles: {
      font: "helvetica",
      fontStyle: "normal",
      fillColor: "#fff",
      textColor: "#1A1421",
      fontSize: 8 * scale,
      halign: "left",
      valign: "top",
      lineColor: "#e0e0e0",
      lineWidth: { top: 0, right: 0, bottom: 0, left: 0 },
      cellPadding: { top: 0, right: 3 * scale, bottom: 2 * scale, left: 2 * scale },
      cellWidth: "auto"
    },
    columnStyles: getTermsAndSignatureColumnStyles(scale),
    willDrawCell: (cell: any) =>
      handleTermsAndSignatureWillDrawCell(cell, lastYRef, lastHeightRef)
  });

  return { lastY: lastYRef.value + lastHeightRef.value + (1 * scale) };
}

async function renderSignature(
  doc: any,
  translate: any,
  headerData: PrintDataEntitas,
  lastY: number,
  scale: number
) {
  const pageWidth = doc.internal.pageSize.width;

  const centerStartXSignature = pageWidth * 0.75;

  // label
  const signatureLabelText = translate("invoice.signatureOfShipper");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8 * scale);
  doc.setTextColor("#1A1421");
  doc.text(signatureLabelText, centerStartXSignature, lastY);

  // signature name
  const padding = 8 * scale;
  const maxSenderName = (60 * scale) - padding;
  const senderName = limitLengthName(
    (headerData.sttSenderName?.trim() || "-").toUpperCase(),
    maxSenderName
  );

  await renderMultilingualText(
    new MultilingualText({
      doc,
      text: senderName,
      product: headerData.sttProductType,
      isInternational: headerData.isInternational,
      startX: centerStartXSignature,
      startY: lastY + (20 * scale),
      maxWidth: maxSenderName,
      fontSize: 8 * scale,
      fontStyle: "bold"
    })
  );
}

const renderCommercialInvoice = async (
  doc: any,
  headerData: PrintDataEntitas,
  logo: string,
  translate: any,
  language: string
) => {
  const scale = doc.internal.pageSize.width / 210;

  renderHeader(doc, translate, logo, scale);
  await renderShipperInfo(doc, translate, headerData, language, scale);
  await renderConsigneeInfo(doc, translate, headerData, scale);

  const { lastY } = await renderMainTable(doc, translate, headerData, language, scale);
  const { lastY: lastYTerms } = renderTerms(doc, translate, headerData, lastY, scale);
  await renderSignature(doc, translate, headerData, lastYTerms, scale);
};

const buildTranslate = (language: string) => {
  const i18nInstance = createI18n({
    legacy: false,
    locale: language,
    fallbackLocale: "id",
    globalInjection: false,
    messages: { en, id, my }
  });
  return i18nInstance.global.t;
};

export const resolveLanguage = (item: PrintDataEntitas) =>
  item.isInternational === true ? "en" : "id";

const generateCommercialInvoice = async (
  headerData: PrintDataEntitas,
  logo: string,
  language = "en",
  pageSize: "a4" | "a6" = "a4"
) => {
  const doc: any = new jsPDF("p", "mm", pageSize, true);
  await renderCommercialInvoice(
    doc,
    headerData,
    logo,
    buildTranslate(language),
    language
  );

  doc.setProperties({ title: "Commercial Invoice" });
  doc.autoPrint();
  window.open(doc.output("bloburl"), "_blank");
};

const generateBulkCommercialInvoice = async (
  data: PrintDataEntitas[],
  logo: string,
  pageSize: "a4" | "a6" = "a4"
) => {
  if (!data?.length) return;

  const doc: any = new jsPDF("p", "mm", pageSize, true);
  for (let i = 0; i < data.length; i++) {
    if (i > 0) doc.addPage(pageSize, "p");
    const language = resolveLanguage(data[i]);
    await renderCommercialInvoice(
      doc,
      data[i],
      logo,
      buildTranslate(language),
      language
    );
  }

  doc.setProperties({ title: "bulk-print-commercial-invoice.pdf" });
  doc.autoPrint();
  window.open(doc.output("bloburl"), "_blank");
};

export default generateCommercialInvoice;
export { generateBulkCommercialInvoice };
